"use client";

import { useContext, useEffect } from "react";
import { notFound } from "next/navigation";

import { QRUrl } from "@/templates/QrEditorTemplates/qr-url";
import { QRVcard } from "@/templates/QrEditorTemplates/qr-vcard";
import { QRSocial } from "@/templates/QrEditorTemplates/qr-social";
import { QRBusiness } from "@/templates/QrEditorTemplates/qr-business";
import { QREvent } from "@/templates/QrEditorTemplates/qr-event";
import { QRLinks } from "@/templates/QrEditorTemplates/qr-listoflinks";
import { QRYoutube } from "@/templates/QrEditorTemplates/qr-youtube";
import { QRWifi } from "@/templates/QrEditorTemplates/qr-wifi";
import { QRApps } from "@/templates/QrEditorTemplates/qr-apps";
import { QRPdf } from "@/templates/QrEditorTemplates/qr-pdf";
import { QRWebsite } from "@/templates/QrEditorTemplates/qr-website";
import { QRImages } from "@/templates/QrEditorTemplates/qr-images";
import { QRVideo } from "@/templates/QrEditorTemplates/qr-video";
import { QRCoupon } from "@/templates/QrEditorTemplates/qr-coupon";
import { QRFeedback } from "@/templates/QrEditorTemplates/qr-feedback";
import { QRText } from "@/templates/QrEditorTemplates/qr-text/qr-text";
import { QREmail } from "@/templates/QrEditorTemplates/qr-email";
import { QRUrlStatic } from "@/templates/QrEditorTemplates/qr-url-static";
import { QRSms } from "@/templates/QrEditorTemplates/qr-sms";
import { QRMp3 } from "@/templates/QrEditorTemplates/qr-mp3";
import { QRWhatsapp } from "@/templates/QrEditorTemplates/qr-whatsapp";

import { PAGES } from "@/internals/constants/pages";

import { CreateContainerProps } from "./types";
import { amplitudeTrackEvent } from "@/services/AmplitudeService/AmplitudeService";
import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";
import { validateEventSource } from "@/utils/qrEditorNavigatorHelper";
import { QREditorContext } from "@/contexts/QREditorContext";

const Components = {
  [PAGES.QR_URL]: QRUrl,
  [PAGES.QR_BUSINESS]: QRBusiness,
  [PAGES.QR_APPS]: QRApps,
  [PAGES.QR_VCARD]: QRVcard,
  [PAGES.QR_SOCIAL]: QRSocial,
  [PAGES.QR_VIDEO]: QRVideo,
  [PAGES.QR_WEBSITE]: QRWebsite,
  [PAGES.QR_EVENT]: QREvent,
  [PAGES.QR_LISTOFLINKS]: QRLinks,
  [PAGES.QR_YOUTUBE]: QRYoutube,
  [PAGES.QR_WIFI]: QRWifi,
  [PAGES.QR_IMAGE]: QRImages,
  [PAGES.QR_PDF]: QRPdf,
  [PAGES.QR_COUPON]: QRCoupon,
  [PAGES.QR_FEEDBACK]: QRFeedback,
  [PAGES.QR_MP3]: QRMp3,
  [PAGES.QR_TEXT]: QRText,
  [PAGES.QR_EMAIL]: QREmail,
  [PAGES.QR_URL_STATIC]: QRUrlStatic,
  [PAGES.QR_SMS]: QRSms,
  [PAGES.QR_WHATSAPP]: QRWhatsapp,
};

export const CreateContainer = ({ data, qrSlug }: CreateContainerProps) => {
  const { source, setQrSelected } = useContext(AmplitudeContext);
  const { setCreateNewTemplate } = useContext(QREditorContext);
  const Renderer = Components[qrSlug];

  if (!Renderer) return notFound();

  useEffect(() => {
    setQrSelected(qrSlug);
    setCreateNewTemplate(false);
    amplitudeTrackEvent("view_editor_add_content", {
      "qr type": qrSlug,
      source: validateEventSource(source),
    });
  }, []);

  return <Renderer data={data} />;
};
